// App.js
import React, { useEffect, useState } from "react";
import {
  AppProvider,
  Page,
  Layout,
  Card,
  Text,
  InlineStack,
  BlockStack,
  Icon,
  Banner,
  Checkbox,
  Tooltip as PolarisTooltip,
  Button
} from "@shopify/polaris";
import { ChartVerticalIcon, ExternalIcon, DeleteIcon } from "@shopify/polaris-icons";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import './app.css';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  validateStatus: (status) => status >= 200 && status < 400
});

const TODAY_SALES_REFRESH_INTERVAL_SECONDS = 60;

import { useRef } from "react";
import debounce from "lodash.debounce";








export default function App() {
  const [loading, setLoading] = useState(true);
  const [storeConnected, setStoreConnected] = useState(true);
  const [salesYesterday, setSalesYesterday] = useState(0);
  const [refundsYesterday, setRefundsYesterday] = useState(0);
  const [onlineStoreYesterday, setOnlineStoreYesterday] = useState(0);
  const [posYesterday, setPosYesterday] = useState(0);
  const [salesMTD, setSalesMTD] = useState(0);
  const [salesMTDWithPOS, setSalesMTDWithPOS] = useState(0);
  const [monthlyGoal, setMonthlyGoal] = useState(0);
  const [planMTD, setPlanMTD] = useState(0);
  const [percentToPlan, setPercentToPlan] = useState(0);
  const [percentToPlanWithPOS, setPercentToPlanWithPOS] = useState(0);
  const [orders, setOrders] = useState([]);
  const [environment, setEnvironment] = useState("unknown");
  const [chartData, setChartData] = useState([]);
  const [salesSummary, setSalesSummary] = useState("");
  const [salesSummaryBullets, setSalesSummaryBullets] = useState([]);
  const [salesCollapsed, setSalesCollapsed] = useState(false);
  const [topProductsMTD, setTopProductsMTD] = useState([]);
  const [showWebTrendline, setShowWebTrendline] = useState(true);
  const [webScenarioKeys, setWebScenarioKeys] = useState([]);
  const [majorProjects, setMajorProjects] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const [dotCount, setDotCount] = useState(0); // For "Saving..."
  const [saveColor, setSaveColor] = useState(undefined);
  const monthName = new Date().toLocaleString("default", { month: "long" });
  const [showCompleted, setShowCompleted] = useState(false);
  const [todaysSales, setTodaysSales] = useState(0);
  const [todaysGoal, setTodaysGoal] = useState(0);
  const [goalYesterday, setGoalYesterday] = useState(0);
  const [ordersCollapsed, setOrdersCollapsed] = useState(true);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [refreshCountdown, setRefreshCountdown] = useState(TODAY_SALES_REFRESH_INTERVAL_SECONDS);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isReevaluating, setIsReevaluating] = useState(false);

  const saveProjectsRef = useRef(
    debounce(async (projects) => {
      await api.post("/api/projects", { projects });
    }, 1000)
  );



  const handleDeleteProject = async (id) => {


    try {
      await api.delete(`/api/projects/${id}`);
      setMajorProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  // 👇 PUT IT HERE (with the others)
  useEffect(() => {
    console.log("STATE UPDATED:", majorProjects);
  }, [majorProjects]);

  async function refreshTodaySales() {
    try {
      const response = await api.get("/api/today-sales");

      if (response.status === 401) {
        setStoreConnected(false);
        return;
      }

      setTodaysSales(response.data.todaySales || 0);
      setRefreshCountdown(TODAY_SALES_REFRESH_INTERVAL_SECONDS);
      setStoreConnected(true);
    } catch (err) {
      console.error("Error refreshing today's sales:", err);
    }
  }

  async function fetchDashboardSummary(chartRows = null, options = {}) {
    const { force = false } = options;

    try {
      const response = await api.get("/api/dashboard-summary", {
        params: force ? { force: 1 } : undefined
      });

      if (response.status >= 200 && response.status < 400) {
        setSalesSummary(response.data?.summary?.text || "");
        setSalesSummaryBullets(Array.isArray(response.data?.summary?.bullets) ? response.data.summary.bullets : []);
        return;
      }
    } catch (err) {
      console.error("Error fetching dashboard summary:", err);
    }

    if (chartRows) {
      setSalesSummary(webSalesTrendSentence(chartRows));
      setSalesSummaryBullets([]);
    }
  }

  async function fetchDashboardData(options = {}) {
    const { showLoadingSpinner = false, todayOnly = false } = options;

    if (showLoadingSpinner) {
      setLoading(true);
    }

    try {
      const response = await api.get("/api/dashboard");

      if (response.status === 401) {
        setStoreConnected(false);
        return;
      }

      const {
        environment: env,
        yesterday,
        refunds,
        monthToDate,
        monthToDateWithPOS,
        yesterdayOrders,
        monthlyGoal: backendGoal,
        onlineStoreYesterday: onlineYesterday,
        posYesterday: posYest,
        topProductsMTD: topProductsFromBackend,
        dailyGoals,
        yearly,
        todaySales
      } = response.data;

      setTodaysSales(todaySales || 0);
      setRefreshCountdown(TODAY_SALES_REFRESH_INTERVAL_SECONDS);

      if (todayOnly) {
        setStoreConnected(true);
        return;
      }

      const today = new Date();
      const yesterdayDate = new Date(today);
      yesterdayDate.setDate(today.getDate() - 1);
      yesterdayDate.setHours(0, 0, 0, 0);

      const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
      const plannedThroughYesterday = backendGoal > 0
        ? (backendGoal / daysInMonth) * yesterdayDate.getDate()
        : 0;

      setSalesYesterday(yesterday || 0);
      setEnvironment(env);
      setRefundsYesterday(refunds || 0);
      setSalesMTD(monthToDate || 0);
      setSalesMTDWithPOS(monthToDateWithPOS || 0);
      setMonthlyGoal(backendGoal || 0);
      setOrders(yesterdayOrders || []);
      setOnlineStoreYesterday(onlineYesterday || 0);
      setPosYesterday(posYest || 0);
      setPlanMTD(plannedThroughYesterday);
      setYearlyStats(yearly || null);

      setPercentToPlan(
        plannedThroughYesterday > 0
          ? ((monthToDate / plannedThroughYesterday) * 100).toFixed(1)
          : 0
      );

      setPercentToPlanWithPOS(
        plannedThroughYesterday > 0
          ? ((monthToDateWithPOS / plannedThroughYesterday) * 100).toFixed(1)
          : 0
      );

      setStoreConnected(true);
      setTopProductsMTD(topProductsFromBackend || []);

      let mtdActual = 0, mtdGoal = 0, mtdWebOnly = 0;

      const sortedGoals = (dailyGoals || []).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      const todayISO = new Date().toLocaleDateString("en-CA");
      const yesterdayISO = yesterdayDate.toLocaleDateString("en-CA");
      const todayGoalRow = sortedGoals.find((g) => g.date === todayISO);
      const yesterdayGoalRow = sortedGoals.find((g) => g.date === yesterdayISO);
      const todayGoalValue = Number(todayGoalRow?.goal) || 0;
      const yesterdayGoalValue = Number(yesterdayGoalRow?.goal) || 0;
      setTodaysGoal(todayGoalValue);
      setGoalYesterday(yesterdayGoalValue);

      const chartDataSafe = (sortedGoals || []).map(dg => {
        const [year, month, day] = dg.date.split("-").map(Number);
        const dt = new Date(year, month - 1, day);
        const isToday = dg.date === todayISO;
        const web = isToday
          ? (todaySales || 0)
          : (dg.channels?.["Online Store"]?.net || 0);
        const pos = isToday
          ? 0
          : (dg.channels?.["Point of Sale"]?.net || 0);

        const total = web + pos;
        const goal = Number(dg.goal) || 0;
        const dailyPercent = goal > 0 ? (total / goal) * 100 : 0;
        const dailyWebPercent = goal > 0 ? (web / goal) * 100 : 0;
        const isFuture = dg.date > todayISO;

        mtdActual += total;
        mtdGoal += goal;
        mtdWebOnly += web;

        return {
          date: dt,
          dateISO: dg.date,
          formattedDate: dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          goal,
          actual: total,
          webOnly: web,
          posOnly: pos,
          dailyPercentOfGoal: isFuture ? null : dailyPercent,
          dailyWebPercentOfGoal: isFuture ? null : dailyWebPercent,
          mtdPercentOfGoal: isFuture ? null : (mtdGoal > 0 ? (mtdActual / mtdGoal) * 100 : 0),
          mtdWebPercentOfGoal: isFuture ? null : (mtdGoal > 0 ? (mtdWebOnly / mtdGoal) * 100 : 0)
        };
      });

      let runningWebPctTotal = 0;
      let runningWebPctCount = 0;

      const finalChartData = chartDataSafe.map((d) => {
        if (d.dailyWebPercentOfGoal !== null && d.dailyWebPercentOfGoal !== undefined) {
          runningWebPctTotal += d.dailyWebPercentOfGoal;
          runningWebPctCount += 1;
        }

        return {
          ...d,
          webTrend: runningWebPctCount > 0 ? (runningWebPctTotal / runningWebPctCount) : null
        };
      });

      const actualWebDayIndexes = finalChartData
        .map((day, index) => (
          day.dailyWebPercentOfGoal !== null && day.dailyWebPercentOfGoal !== undefined
            ? index
            : null
        ))
        .filter((index) => index !== null);

      const scenarioKeys = actualWebDayIndexes
        .slice(0, -1)
        .map((_, scenarioIndex) => `webScenario${scenarioIndex + 1}`);

      const lastChartIndex = finalChartData.length - 1;
      const chartWithScenarios = finalChartData.map((day) => ({ ...day }));

      actualWebDayIndexes.slice(0, -1).forEach((startIndex, scenarioIndex) => {
        const scenarioKey = `webScenario${scenarioIndex + 1}`;
        const startValue = chartWithScenarios[startIndex].dailyWebPercentOfGoal;
        const projectedFinish = chartWithScenarios[startIndex].webTrend;
        const remainingPoints = Math.max(lastChartIndex - startIndex, 1);

        for (let pointIndex = startIndex; pointIndex <= lastChartIndex; pointIndex += 1) {
          const progress = (pointIndex - startIndex) / remainingPoints;
          const easedProgress = 1 - ((1 - progress) ** 2);

          chartWithScenarios[pointIndex][scenarioKey] = startValue + (
            (projectedFinish - startValue) * easedProgress
          );
        }
      });

      setWebScenarioKeys(scenarioKeys);
      setChartData(chartWithScenarios);
      await fetchDashboardSummary(chartWithScenarios);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setStoreConnected(false);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    fetchDashboardData({ showLoadingSpinner: true });
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTodaySales();
    }, TODAY_SALES_REFRESH_INTERVAL_SECONDS * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const countdownId = setInterval(() => {
      setRefreshCountdown(prev =>
        prev > 0 ? prev - 1 : 0
      );
    }, 1000);

    return () => clearInterval(countdownId);
  }, []);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await api.get("/api/projects");
        const projectsWithTempId = res.data.projects.map(p => ({
          ...p,
          tempId: p.tempId || uuidv4()   // generate tempId if missing
        }));
        setMajorProjects(projectsWithTempId);
      } catch (err) {
        console.error(err);
      }
    }
    fetchProjects();
  }, []);

  useEffect(() => {
    if (saveStatus !== "saving") {
      setDotCount(0);
      return; // no interval needed
    }

    const intervalId = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4); // cycles 0 → 3 dots
    }, 500);

    return () => clearInterval(intervalId); // cleanup
  }, [saveStatus]);

  const formatCurrency = (num) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num || 0);

  const refreshLabel = `Refreshing sales in (${refreshCountdown}s)`;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchDashboardData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleReevaluate = async () => {
    setIsReevaluating(true);
    try {
      await fetchDashboardSummary(chartData, { force: true });
    } finally {
      setIsReevaluating(false);
    }
  };

  const tone = percentToPlan >= 100 ? "success" : percentToPlan >= 80 ? "caution" : "critical";
  const getKey = (p) => p.id ?? p.tempId;

  return (
    <AppProvider>
      <Page
        fullWidth
        title={
          <InlineStack align="start" gap="200">
            <Text variant="headingLg" as="h1">
              {monthName} Sales Goal {formatCurrency(monthlyGoal)}
            </Text>
            {environment !== "production" && (
              <Text tone="critical" variant="bodySm" as="span">DEV</Text>
            )}
          </InlineStack>
        }
        subtitle="The Road to a Million Dollars"
        primaryAction={{ content: refreshLabel, onAction: handleRefresh, loading: isRefreshing }}
      >



        {!storeConnected && (
          <Banner status="critical" title="Store not connected">
            <p>
              Connect your Shopify store to see sales data.{" "}
              <a href={`/auth?shop=${process.env.REACT_APP_SHOPIFY_STORE}`} style={{ color: "#008060", textDecoration: "underline" }}>
                Connect Store
              </a>
            </p>
          </Banner>
        )}
        <Layout>
          {/* Metrics Section */}
          <Layout.Section variant="fullWidth">
            <div className="metrics-four-up">
              <Card sectioned>
                <MetricTile
                  label="Yesterday's Net Sales"
                  value={formatCurrency(salesYesterday)}
                  subValue={`Goal: ${formatCurrency(goalYesterday)}`}

                  sideLabel="Placeholder"
                  sideValue={`${goalYesterday > 0 ? ((salesYesterday / goalYesterday) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(salesMTDWithPOS)}
                  sideTone={tone}
                />
              </Card>
              <Card sectioned>
                <MetricTile
                  label="Sales Month To Date"
                  value={formatCurrency(salesMTD)}
                  subValue={`MTD Target: ${formatCurrency(planMTD)}`}

                  sideLabel="Placeholder"
                  sideValue={`${planMTD > 0 ? ((salesMTD / planMTD) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(salesMTDWithPOS)}
                  sideTone={tone}
                />
              </Card>

              <Card sectioned>
                <MetricTile
                  label="Today's Net Sales"
                  value={formatCurrency(todaysSales)}
                  subValue={`Goal: ${formatCurrency(todaysGoal)}`}
                  sideLabel="Placeholder"
                  sideValue={`${todaysGoal > 0 ? ((todaysSales / todaysGoal) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(salesMTDWithPOS)}
                  sideTone={tone}
                />
              </Card>

              <Card sectioned>
                <MetricTile
                  label="Sales Year To Date"
                  value={`${formatCurrency(yearlyStats?.yearToDate)}`}
                  subValue={`YTD Target: ${formatCurrency(yearlyStats?.ytdPlan)}`}
                  sideLabel="Pace"
                  sideValue={`${(yearlyStats?.ytdPlan ?? 0) > 0 ? (((yearlyStats?.yearToDate ?? 0) / yearlyStats.ytdPlan) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(yearlyStats?.goal)}
                  sideTone={(yearlyStats?.ytdPlan ?? 0) > 0 && (yearlyStats?.yearToDate ?? 0) >= yearlyStats.ytdPlan ? "success" : "critical"}
                />
              </Card>

            </div>
          </Layout.Section>
          <Layout.Section variant="fullWidth">
            <div className="dashboard-grid">
              <div className="dashboard-item span-half">
                <Card sectioned>
                  <button
                    onClick={() => setSalesCollapsed(!salesCollapsed)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'block',
                      marginBottom: 4,

                    }}
                  >
                    Sales Yesterday (Net) — {formatCurrency(salesYesterday)}
                  </button>
                  {salesCollapsed && (
                    <BlockStack gap="100" style={{ paddingLeft: 16, flexDirection: 'column' }}>
                      <Metric label="Online Store Yesterday" value={formatCurrency(onlineStoreYesterday)} />
                      <Metric label="POS Yesterday" value={formatCurrency(posYesterday)} />
                      <Metric label="Refunds Yesterday" value={formatCurrency(refundsYesterday)} />
                      <Metric label="POS Month to Date" value={formatCurrency(salesMTDWithPOS - salesMTD)} />
                      <Metric label="Plan to Date" value={formatCurrency(planMTD)} />
                    </BlockStack>
                  )}
                  <Metric label="Monthly Goal" value={formatCurrency(monthlyGoal)} />
                  <Metric
                    label="% to Plan (Web Only)"
                    value={`${percentToPlan}% (${formatCurrency(salesMTD)})`}
                    tone={percentToPlan > 99 ? "success" : percentToPlan < 99 ? "critical" : tone}
                  />
                  <Metric
                    label="% to Plan (Including POS)"
                    value={`${percentToPlanWithPOS}% (${formatCurrency(salesMTDWithPOS)})`}
                    tone={percentToPlan > 99 ? "success" : percentToPlan < 99 ? "critical" : tone}
                  />

                  {yearlyStats && (
                    <>
                      <Metric label="YTD Progress" value={`${yearlyStats.progressPct}% (${formatCurrency(yearlyStats.yearToDate)})`} />
                      <Metric label="Projected Year End" value={formatCurrency(yearlyStats.projectedYearEnd)} tone={yearlyStats.projectedYearEnd >= yearlyStats.goal ? "success" : "critical"} />
                    </>
                  )}

                </Card>
              </div>


              {/* Orders Section */}
              <div className="dashboard-item span-half">
                <Card sectioned>
                  <BlockStack gap="6"> {/* reasonable gap */}
                    {/* Header with button */}
                    <InlineStack align="space-between">
                      <Text variant="headingMd" as="h2">
                        Orders Yesterday
                      </Text>
                      {orders.length > 3 && (
                        <Button plain className="toggle-orders-btn" onClick={() => setOrdersCollapsed(!ordersCollapsed)}>
                          {ordersCollapsed ? `+${orders.length - 3} more` : '-Collapse'}
                        </Button>
                      )}
                    </InlineStack>

                    {/* Orders list */}
                    {loading ? (
                      <Text>Loading…</Text>
                    ) : orders.length ? (
                      <div className="orders-container">
                        {orders.map((order, idx) => {
                          const isExtra = idx >= 3;
                          return (
                            <div
                              key={order.id}
                              className={`order-item ${isExtra ? 'extra' : ''} ${!ordersCollapsed && isExtra ? 'show' : ''
                                }`}
                            >
                              <InlineStack align="space-between">
                                <a
                                  href={`https://admin.shopify.com/store/${process.env.REACT_APP_SHOPIFY_STORE}/orders/${order.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Text tone="brand">{order.name}</Text>
                                </a>
                                <Text>{formatCurrency(order.financials.netYesterday)}</Text>
                              </InlineStack>

                              {order.financials.refundsYesterday > 0 && (
                                <InlineStack align="space-between">
                                  <Text tone="critical">Refunds</Text>
                                  <Text tone="critical">
                                    -{formatCurrency(order.financials.refundsYesterday)}
                                  </Text>
                                </InlineStack>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <BlockStack gap="2">
                        <Text>No orders available</Text>
                        <a
                          href={`/auth?shop=${process.env.REACT_APP_SHOPIFY_STORE}`}
                          style={{ color: "#008060", textDecoration: "underline" }}
                        >
                          Connect Store
                        </a>
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              </div>

              <div className="dashboard-item span-half">
                <Card sectioned>
                  <BlockStack gap="100">
                    <Text variant="headingMd" as="h2">Today's Scheduled Tech Projects</Text>
                    <Checkbox
                      label="Show Completed History"
                      checked={showCompleted}
                      onChange={(val) => setShowCompleted(val)}
                    />
                    <DragDropContext
                      onDragEnd={async (result) => {
                        if (!result.destination) return;

                        // Reorder majorProjects directly
                        const updatedProjects = Array.from(majorProjects);
                        const [moved] = updatedProjects.splice(result.source.index, 1);
                        updatedProjects.splice(result.destination.index, 0, moved);

                        // Update sort_order
                        const updatedWithOrder = updatedProjects.map((p, idx) => ({
                          ...p,
                          sort_order: idx
                        }));

                        setMajorProjects(updatedWithOrder);

                        const payload = updatedWithOrder.map((p, idx) => ({
                          id: p.id || null,
                          tempId: p.tempId || null,
                          project_name: p.project_name || "",
                          completed_date: p.completed_date || null,
                          sort_order: idx
                        }));

                        saveProjectsRef.current(payload);


                      }}
                    >
                      <Droppable droppableId="projects">
                        {(provided) => (
                          <div {...provided.droppableProps} ref={provided.innerRef}>
                            {majorProjects.map((proj, idx) => {
                              const visible = isVisibleProject(proj, showCompleted);
                              return (
                                <Draggable
                                  key={getKey(proj)}
                                  draggableId={`proj-${getKey(proj)}`}
                                  index={idx}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        display: visible ? "flex" : "none",
                                        marginBottom: 4,
                                        position: "relative",
                                        padding: "6px 8px",
                                        borderRadius: 6,
                                        background: snapshot.isDragging ? "#f4f6f8" : "#fff",
                                        border: "none",
                                        background: "rgb(244 244 244)",
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      {/* Drag Handle */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: "40px",
                                          top: "13px",
                                          cursor: "grab",
                                        }}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                          <circle cx="5" cy="5" r="2" fill="#666" />
                                          <circle cx="5" cy="12" r="2" fill="#666" />
                                          <circle cx="5" cy="19" r="2" fill="#666" />
                                          <circle cx="12" cy="5" r="2" fill="#666" />
                                          <circle cx="12" cy="12" r="2" fill="#666" />
                                          <circle cx="12" cy="19" r="2" fill="#666" />
                                        </svg>
                                      </div>

                                      {/* Completed Checkbox */}
                                      <Checkbox
                                        checked={!!proj.completed_date}
                                        onChange={(checked) => {
                                          const now = checked ? localToMySQL(new Date()) : null;
                                          setMajorProjects(prev =>
                                            prev.map(p =>
                                              (p.id ?? p.tempId) === (proj.id ?? proj.tempId)
                                                ? { ...p, completed_date: now }
                                                : p
                                            )
                                          );
                                        }}
                                      />



                                      {/* Project Name */}
                                      {proj.completed_date && (
                                        <Tooltip
                                          content={`Completed at: ${new Date(proj.completed_date).toLocaleString("en-US", {
                                            timeZone: "America/Chicago",
                                            month: "numeric",
                                            day: "numeric",
                                            year: "2-digit",
                                            hour: "numeric",
                                            minute: "2-digit",
                                            hour12: true,
                                          })}`}
                                        >
                                          <span style={{ cursor: 'pointer', align: "center", paddingtop: "2px" }}>
                                            {/* Inline calendar SVG */}
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                              <rect x="3" y="4" width="18" height="18" stroke="#666" strokeWidth="2" />
                                              <line x1="3" y1="10" x2="21" y2="10" stroke="#666" strokeWidth="2" />
                                              <line x1="8" y1="4" x2="8" y2="0" stroke="#666" strokeWidth="2" />
                                              <line x1="16" y1="4" x2="16" y2="0" stroke="#666" strokeWidth="2" />
                                            </svg>
                                          </span>
                                        </Tooltip>
                                      )}
                                      <input
                                        value={proj.project_name || ""}
                                        onChange={(e) => {
                                          setMajorProjects(prev =>
                                            prev.map(p =>
                                              (p.id ?? p.tempId) === (proj.id ?? proj.tempId)
                                                ? { ...p, project_name: e.target.value }
                                                : p
                                            )
                                          );
                                        }}
                                        style={{
                                          flex: 1,
                                          padding: "6px 8px 6px 30px",
                                          border: "unset",
                                          textDecoration: isToday(proj.completed_date) ? "line-through" : "none",
                                          opacity: proj.completed_date ? 0.6 : 1
                                        }}
                                      />

                                      {/* Completed Date */}
                                      <ProjectDateTooltip completedDate={proj.completed_date} />

                                      {/* Delete Button */}
                                      <button
                                        onClick={() => {
                                          if (proj.id) {
                                            handleDeleteProject(proj.id);
                                          } else {
                                            setMajorProjects(prev => prev.filter(p => p.tempId !== proj.tempId));
                                          }
                                        }}
                                        style={{
                                          /* position: "absolute",
                                           zIndex: 10,*/
                                          border: "none",
                                          background: "none",
                                          color: "#666",
                                          right: "3px",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <Icon source={DeleteIcon} />
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>



                    {/* Add Project Button */}
                    <button
                      onClick={() =>
                        setMajorProjects(prev => [
                          ...prev,
                          {
                            tempId: uuidv4(), // or uuidv4()
                            project_name: "",
                            completed_date: null
                          }
                        ])
                      }
                      style={{
                        marginTop: "0px",
                        padding: "6px 8px",
                        background: "none",
                        border: "1px solid #777",
                        cursor: "pointer"
                      }}
                    >
                      + Add Project
                    </button>





                    <Button
                      variant="primary"
                      disabled={saveStatus === "saving"}
                      onClick={async () => {
                        setSaveStatus("saving");
                        setSaveColor(undefined);

                        try {
                          // 1️⃣ Prepare payload: only send fields backend expects
                          const payload = majorProjects.map((p, idx) => ({
                            id: p.id || null,
                            tempId: p.tempId || null,   // 👈 ADD THIS
                            project_name: p.project_name || "",
                            completed_date: p.completed_date || null,
                            sort_order: p.sort_order ?? idx
                          }));

                          console.log("Sending projects to backend:", payload);

                          // 2️⃣ Post to backend
                          const saveRes = await api.post("/api/projects", { projects: payload });

                          // 3️⃣ Backend should return full list with real MySQL IDs
                          const refreshed = await api.get("/api/projects");

                          // 4️⃣ Map backend IDs back into frontend state
                          setMajorProjects(
                            refreshed.data.projects
                              .sort((a, b) => a.sort_order - b.sort_order) // 👈 safety net
                              .map(p => ({
                                id: p.id,
                                tempId: p.temp_id || uuidv4(), // 👈 keep identity alive
                                project_name: p.project_name,
                                completed_date: p.completed_date,
                                sort_order: p.sort_order
                              }))
                          );

                          setSaveStatus("saved");
                          setSaveColor("#28a745");

                          setTimeout(() => {
                            setSaveStatus("idle");
                            setSaveColor(undefined);
                          }, 2000);

                        } catch (err) {
                          console.error("Failed saving projects:", err);
                          setSaveStatus("idle");
                          console.log("Sending projects to backend:", payload);
                        }
                      }}
                    >
                      {saveStatus === "idle" && "Save Projects"}
                      {saveStatus === "saving" && `Saving${'.'.repeat(dotCount)}`}
                      {saveStatus === "saved" && "Saved"}

                    </Button>

                  </BlockStack>
                </Card>
              </div>

              {/* Top Products MTD Section */}
              <div className="dashboard-item span-half">
                <Card sectioned>
                  <BlockStack gap="200">
                    <Text variant="headingMd" as="h2">
                      Top Products Month-to-Date
                    </Text>

                    {topProductsMTD.length ? (
                      topProductsMTD.map((p, idx) => (
                        <BlockStack key={idx} gap="50">
                          <InlineStack align="center" style={{ width: "100%" }}>
                            <a
                              href={`https://admin.shopify.com/store/${process.env.REACT_APP_SHOPIFY_STORE}/products/${p.productId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textDecoration: "none" }}
                            >
                              <Text tone="brand">{p.title}</Text>
                            </a>

                            <a
                              href={`https://${process.env.REACT_APP_SHOPIFY_STORE}.myshopify.com/products/${p.productId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: "flex", alignItems: "center", textDecoration: "none", marginLeft: 12, marginRight: "auto" }}
                            >
                              <Text tone="brand">Store</Text>
                              <Icon source={ExternalIcon} color="base" style={{ marginLeft: 4 }} />
                            </a>

                            <Text variant="headingSm">
                              {formatCurrency(p.revenue)} (qty: {p.quantity})
                            </Text>
                          </InlineStack>
                        </BlockStack>
                      ))
                    ) : (
                      <Text tone="subdued">No product sales this month</Text>
                    )}
                  </BlockStack>
                </Card>
              </div>
            </div>
          </Layout.Section>

          {/* Percent-of-Goal Chart by Channel */}

          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <InlineStack align="space-between" style={{ width: "100%", marginBottom: 16 }}>
                <Text variant="headingMd">Daily % of Goal Achieved</Text>
                <InlineStack gap="300" blockAlign="center">
                  <Button onClick={handleReevaluate} loading={isReevaluating}>
                    Re-evaluate
                  </Button>
                  <Checkbox
                    label="Show Projected Finish Pace"
                    checked={showWebTrendline}
                    onChange={(newValue) => setShowWebTrendline(newValue)}
                  />
                </InlineStack>
              </InlineStack>
              {/* Web Sales Trend Sentence */}
              <Text variant="heading" style={{ marginBottom: 16 }}>
                {salesSummary || webSalesTrendSentence(chartData)}
              </Text>
              {salesSummaryBullets.length > 0 && (
                <BlockStack gap="100" style={{ marginBottom: 16 }}>
                  {salesSummaryBullets.map((bullet, index) => (
                    <Text key={`${index}-${bullet}`} tone="subdued">
                      {"\u2022 "}
                      <GlossaryText text={bullet} />
                    </Text>
                  ))}
                </BlockStack>
              )}

              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="formattedDate" />
                    <YAxis unit="%" domain={[0, 'auto']} />
                    <Tooltip content={<GoalChartTooltip />} />
                    <Legend formatter={(value) => value && value.startsWith("webScenario") ? "" : value} />
                    <Line type="monotone" dataKey="dailyPercentOfGoal" name="Daily % of Goal" stroke="#82ca9d" strokeWidth={2} dot={true} />
                    <Line type="monotone" dataKey="dailyWebPercentOfGoal" name="Daily Web % of Goal" stroke="#FFA500" strokeWidth={2} dot={true} />
                    {showWebTrendline && (
                      <>
                        {webScenarioKeys.map((scenarioKey) => (
                          <Line
                            key={scenarioKey}
                            type="monotone"
                            dataKey={scenarioKey}
                            name={false}
                            stroke="#0000004f"
                            strokeWidth={1}
                            strokeOpacity={1}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls={false}
                          />
                        ))}
                        <Line type="linear" dataKey="webTrend" name="Projected Finish Pace" stroke="#FF9900" strokeWidth={2} dot={false} strokeDasharray="6 6" isAnimationActive={true} />
                      </>)}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text>No data for chart</Text>
              )}
            </Card>
          </Layout.Section>

          {/* Daily Sales vs Goal Chart */}
          {/* Daily Sales vs Goal Chart */}
          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <Text variant="headingMd" as="h2">Daily Sales vs Goal</Text>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="formattedDate" />
                    <YAxis
                      tickFormatter={(value) =>
                        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value)
                      }
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value),
                        name === 'webOnly' ? 'Web Only' : name
                      ]}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="goal" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="actual" stroke="#82ca9d" strokeWidth={2} />
                    <Line type="monotone" dataKey="webOnly" stroke="#FFA500" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text>No data for chart</Text>
              )}
            </Card>
          </Layout.Section>





          <Layout.Section variant="fullWidth">
            <Text variant="bodySm" tone="subdued">Environment: {environment.toUpperCase()}</Text>
          </Layout.Section>
        </Layout>
      </Page>
    </AppProvider>
  );
}

const isToday = (dateStr) => {
  if (!dateStr) return false;

  const today = new Date();
  const d = new Date(dateStr);

  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
};

const isVisibleProject = (p, showCompleted) => {
  return !p.completed_date || isToday(p.completed_date) || showCompleted;
};

function Metric({ label, value, tone }) {
  return (
    <InlineStack align="space-between">
      <Text>{label}</Text>
      <Text variant="headingLg" tone={tone}>{value}</Text>
    </InlineStack>
  );
}

function MetricTile({
  label,
  value,
  subValue,
  tone,
  sideLabel,
  sideValue,
  sideSubValue,
  sideTone
}) {
  return (
    <InlineStack align="space-between" blockAlign="start" style={{ width: "100%" }}>
      <BlockStack gap="150">
        <Text variant="bodySm" tone="subdued">{label}</Text>
        <Text variant="headingLg" tone={tone}>{value}</Text>
        {subValue ? (
          <Text variant="bodySm" tone="subdued">{subValue}</Text>
        ) : null}
      </BlockStack>
      {sideLabel || sideValue || sideSubValue ? (
        <div className="metric-tile-side">
          <BlockStack gap="150">
            <Text variant="bodySm" tone="subdued">{sideLabel}</Text>
            <Text variant="headingLg" tone={sideTone}>{sideValue}</Text>
            <Text variant="bodySm" tone="subdued">{sideSubValue}</Text>
          </BlockStack>
        </div>
      ) : null}
    </InlineStack>
  );
}

function localToMySQL(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function webSalesTrendSentence(chartData) {
  if (!chartData || chartData.length === 0) return "No web sales data available.";

  const validData = chartData.filter(d => d.mtdWebPercentOfGoal !== undefined);
  if (validData.length < 2) return "Not enough data to calculate trend.";

  // Calculate "below/above goal" percentages
  const startPctBelow = 100 - validData[0].mtdWebPercentOfGoal;
  const endPctBelow = 100 - validData[validData.length - 1].mtdWebPercentOfGoal;

  const startWord = startPctBelow >= 0 ? "below" : "above";
  const endWord = endPctBelow >= 0 ? "below" : "above";

  // Rate of change
  const changePct = endPctBelow - startPctBelow;
  const daysElapsed = validData.length - 1;
  const dailyRate = changePct / daysElapsed;
  const monthlyRate = dailyRate * 30;

  const droppingIncreasing = dailyRate >= 0 ? "dropping" : "increasing";

  return `Web sales started the month at ${Math.abs(startPctBelow).toFixed(0)}% ${startWord} goal and are now ${Math.abs(endPctBelow).toFixed(0)}% ${endWord} goal, ${droppingIncreasing} at a rate of ${Math.abs(dailyRate).toFixed(2)}% per day (${Math.abs(monthlyRate).toFixed(1)}%/month).`;
}

function ProjectDateTooltip({ completedDate }) {
  if (!completedDate) return null;

  const displayText = `Completed at: ${new Date(completedDate).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;

  return (
    <PolarisTooltip content={displayText}>
      <span style={{ cursor: "pointer", position: "relative", top: "4px", left: "4px" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" stroke="#666" strokeWidth="2" />
          <line x1="3" y1="10" x2="21" y2="10" stroke="#666" strokeWidth="2" />
          <line x1="8" y1="4" x2="8" y2="0" stroke="#666" strokeWidth="2" />
          <line x1="16" y1="4" x2="16" y2="0" stroke="#666" strokeWidth="2" />
        </svg>
      </span>
    </PolarisTooltip>
  );
}

const GLOSSARY_TERMS = {
  outlier: "An outlier is a data point that is significantly different from the rest of the data in a set. It stands out because it is much higher, much lower, or otherwise unusual compared to the typical range of values. Historicall we have experieced 1 or 2 ouliers per month that can represent 20% or more of our monthly goal.",
  outliers: "An outlier is a data point that is significantly different from the rest of the data in a set. It stands out because it is much higher, much lower, or otherwise unusual compared to the typical range of values. Historicall we have experieced 1 or 2 ouliers per month that can represent 20% or more of our monthly goal."
};

function GlossaryText({ text }) {
  const parts = String(text || "").split(/(\boutliers?\b)/gi);

  return parts.map((part, index) => {
    const glossaryText = GLOSSARY_TERMS[part.toLowerCase()];

    if (!glossaryText) {
      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    }

    return (
      <PolarisTooltip key={`${part}-${index}`} content={glossaryText}>
        <span
          style={{
            textDecoration: "underline dotted",
            textUnderlineOffset: "2px",
            cursor: "help"
          }}
        >
          {part}
        </span>
      </PolarisTooltip>
    );
  });
}

function GoalChartTooltip({ active, label, payload }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const sortedPayload = [...payload]
    .filter((entry) => entry?.value !== null && entry?.value !== undefined)
    .sort((a, b) => {
      if (b.value !== a.value) {
        return b.value - a.value;
      }

      if (a.dataKey === "webTrend") return -1;
      if (b.dataKey === "webTrend") return 1;

      return 0;
    });

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        padding: "10px 12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
      }}
    >
      <div style={{ marginBottom: 8 }}>{label}</div>
      {sortedPayload.map((entry) => (
        <div key={entry.dataKey} style={{ color: entry.color, marginBottom: 6 }}>
          {entry.name}: {Number(entry.value).toFixed(1)}%
        </div>
      ))}
    </div>
  );
}
